import React, { useRef, useEffect } from 'react';
import { Group, Rect, Text, Line } from 'react-konva';
import { useEditorStore } from '../store/useEditorStore';
import {
  konvaFontStyleFromTypography,
  ensureKonvaFontLoaded,
  refreshKonvaTextStyles,
  isSystemFont,
} from '../utils/googleFonts';
import { computeTextUnderlineSegments } from '../utils/textUnderline';

const MIN_SIZE = 24;

/** Konva text box: fixed frame + clipped preview from layout engine lines. */
export default function KonvaTextBox({ node, isSelected, onLayoutChange }) {
  const groupRef = useRef(null);
  const clickRef = useRef({ id: null, time: 0 });
  const selectObject = useEditorStore((s) => s.selectObject);
  const startTextEdit = useEditorStore((s) => s.startTextEdit);
  const updateObjectTransform = useEditorStore((s) => s.updateObjectTransform);
  const editingTextId = useEditorStore((s) => s.editingTextId);

  const { id, transform: t, typography: ty, text, layout, constraints, backgroundFill } = node;
  const pad = constraints?.padding ?? 2;
  const innerW = Math.max(1, (t.width || 100) - pad * 2);
  const innerH = Math.max(1, (t.height || 40) - pad * 2);
  const fs = layout?.fontSize || ty.fontSize || 12;
  const lhMult = ty.lineHeight > 0 ? ty.lineHeight : 1.16;
  const lineList = Array.isArray(layout?.lines) && layout.lines.length > 0 ? layout.lines : null;
  const displayText = lineList ? lineList.join('\n') : String(text ?? '');
  const underlineLines = lineList || (displayText ? displayText.split('\n') : ['']);
  const underlineSegments = computeTextUnderlineSegments(
    underlineLines,
    ty,
    fs,
    lhMult,
    pad,
    innerW,
  );

  useEffect(() => {
    if (isSelected && groupRef.current) {
      groupRef.current.getStage()?.batchDraw();
    }
  }, [isSelected]);

  useEffect(() => {
    const family = ty.fontFamily;
    if (!family || isSystemFont(family)) return undefined;
    let cancelled = false;
    void ensureKonvaFontLoaded(family, ty).then(() => {
      if (cancelled) return;
      const stage = groupRef.current?.getStage();
      if (!stage) return;
      refreshKonvaTextStyles(stage, [node]);
      stage.batchDraw();
    });
    return () => {
      cancelled = true;
    };
  }, [id, ty.fontFamily, ty.fontWeight, ty.fontStyle]);

  return (
    <Group
      ref={groupRef}
      id={id}
      name={id}
      x={t.x}
      y={t.y}
      rotation={t.rotation || 0}
      clipX={0}
      clipY={0}
      clipWidth={t.width}
      clipHeight={t.height}
      draggable={editingTextId !== id}
      onMouseDown={(e) => {
        e.cancelBubble = true;
      }}
      onClick={(e) => {
        e.cancelBubble = true;
        const now = Date.now();
        const additive = !!(e.evt?.shiftKey || e.evt?.metaKey || e.evt?.ctrlKey);
        if (clickRef.current.id === id && now - clickRef.current.time < 450 && !additive) {
          clickRef.current = { id: null, time: 0 };
          startTextEdit(id);
          return;
        }
        clickRef.current = { id, time: now };
        selectObject(id, { additive });
      }}
      onTap={(e) => {
        e.cancelBubble = true;
        selectObject(id);
      }}
      onDblClick={(e) => {
        e.cancelBubble = true;
        startTextEdit(id);
      }}
      onDblTap={(e) => {
        e.cancelBubble = true;
        startTextEdit(id);
      }}
      onDragEnd={(e) => {
        updateObjectTransform(id, { x: e.target.x(), y: e.target.y() });
        onLayoutChange?.();
      }}
      onTransformEnd={(e) => {
        const g = e.target;
        const scaleX = g.scaleX();
        const scaleY = g.scaleY();
        const newW = Math.max(MIN_SIZE, (t.width || 100) * scaleX);
        const newH = Math.max(MIN_SIZE, (t.height || 40) * scaleY);
        g.scaleX(1);
        g.scaleY(1);
        updateObjectTransform(id, {
          x: g.x(),
          y: g.y(),
          width: newW,
          height: newH,
          rotation: g.rotation(),
        });
        const node = useEditorStore.getState().getObjectById(id);
        if (node?.type === 'text') {
          useEditorStore.getState().updateTextNode(id, {});
        }
        onLayoutChange?.();
      }}
    >
      <Rect
        width={t.width}
        height={t.height}
        fill={backgroundFill || 'rgba(255,255,255,0.001)'}
        stroke={isSelected ? '#2a318b' : 'rgba(148, 163, 184, 0.55)'}
        strokeWidth={isSelected ? 2 : 1}
        listening
      />
      <Text
        key={`${id}-${displayText.slice(0, 48)}-${innerW}-${fs}-${ty.fontFamily}-${ty.fontWeight}-${ty.fontStyle}-${ty.underline ? 1 : 0}`}
        x={pad}
        y={pad}
        width={innerW}
        height={innerH}
        text={displayText}
        fontFamily={ty.fontFamily || 'Arial'}
        fontSize={fs}
        fontStyle={konvaFontStyleFromTypography(ty)}
        fill={ty.fill || '#111827'}
        align={ty.textAlign || 'left'}
        verticalAlign="top"
        lineHeight={lhMult}
        wrap={lineList ? 'none' : 'word'}
        listening={false}
        visible={editingTextId !== id}
      />
      {underlineSegments.map((seg) => (
        <Line
          key={`ul-${id}-${seg.key}`}
          points={[seg.x1, seg.y, seg.x2, seg.y2]}
          stroke={seg.stroke}
          strokeWidth={seg.sw}
          listening={false}
          visible={editingTextId !== id}
        />
      ))}
    </Group>
  );
}
